import type {
  PlayEvent,
  PlayEventBenefit,
  PlayEventParticipant,
  PlayParticipantType,
} from '../types/playEvent';

export interface PlayEventMembershipAddonContext {
  playEventBenefit: PlayEventBenefit;
  playEventDiscountPercent?: number | null;
}

export interface PlayEventPriceInput {
  event: Pick<
    PlayEvent,
    'memberPriceCents' | 'studentPriceCents' | 'guestPriceCents' | 'paymentMode'
  >;
  participantType: PlayParticipantType;
  membershipAddon?: PlayEventMembershipAddonContext | null;
}

export interface PlayEventPriceResult {
  basePriceCents: number;
  finalPriceCents: number;
  isFree: boolean;
  benefitApplied: PlayEventBenefit;
  discountPercent: number;
}

function basePriceForType(
  event: PlayEventPriceInput['event'],
  participantType: PlayParticipantType
): number {
  if (participantType === 'member') return event.memberPriceCents ?? 0;
  if (participantType === 'student') return event.studentPriceCents ?? 0;
  return event.guestPriceCents ?? 0;
}

export function applyPercentDiscount(priceCents: number, percent: number): number {
  if (percent <= 0) return priceCents;
  if (percent >= 100) return 0;
  return Math.round(priceCents * (1 - percent / 100));
}

export function resolvePlayEventPrice(input: PlayEventPriceInput): PlayEventPriceResult {
  const basePriceCents = basePriceForType(input.event, input.participantType);
  const addon = input.membershipAddon;

  if (
    input.participantType === 'member' &&
    addon?.playEventBenefit === 'full_coverage'
  ) {
    return {
      basePriceCents,
      finalPriceCents: 0,
      isFree: true,
      benefitApplied: 'full_coverage',
      discountPercent: 100,
    };
  }

  let discountPercent = 0;
  if (
    input.participantType === 'member' &&
    addon?.playEventBenefit === 'discount_percent' &&
    addon.playEventDiscountPercent != null
  ) {
    discountPercent = addon.playEventDiscountPercent;
  }

  const finalPriceCents = applyPercentDiscount(basePriceCents, discountPercent);
  return {
    basePriceCents,
    finalPriceCents,
    isFree: finalPriceCents === 0,
    benefitApplied: discountPercent > 0 ? 'discount_percent' : 'none',
    discountPercent,
  };
}

export function canPlaceParticipantInMatch(
  participant: Pick<PlayEventParticipant, 'paymentStatus' | 'status'>,
  finalPriceCents: number
): boolean {
  if (participant.status !== 'checked_in' && participant.status !== 'registered') {
    return false;
  }
  if (finalPriceCents === 0) return true;
  return (
    participant.paymentStatus === 'paid' ||
    participant.paymentStatus === 'comped' ||
    participant.paymentStatus === 'waived'
  );
}

export function normalizePlayContactEmail(email?: string | null): string | null {
  const trimmed = email?.trim().toLowerCase();
  return trimmed || null;
}
